import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useIntl } from 'react-intl';

import { Button } from 'src/registry/ui/Button';
import { Input } from 'src/registry/ui/Input';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { cn } from 'src/utils/shadcn';

/**
 * Read-only input with a copy-to-clipboard button.
 *
 * Built for values that have to be transcribed into an external device's portal, where retyping
 * an API key or a UUID path by hand is the likeliest way to get a broken install.
 */
function InputWithCopy({
    wrapperProps,
    ...props
}: React.ComponentProps<typeof Input> & { wrapperProps?: React.ComponentProps<'div'> }) {
    const { formatMessage } = useIntl();
    const [copied, setCopied] = React.useState<boolean>(false);

    // Clears the confirmation state, and cancels the pending timer if the field is copied again
    // or unmounts before it fires.
    React.useEffect(() => {
        if (!copied) return;

        const timeout = setTimeout(() => setCopied(false), 2000);

        return () => clearTimeout(timeout);
    }, [copied]);

    const handleCopy = React.useCallback(() => {
        void navigator.clipboard
            .writeText(String(props.value ?? ''))
            .then(() => setCopied(true))
            .catch((error) => console.warn('copy error', error));
    }, [props.value]);

    return (
        <div className={cn('group relative')} {...wrapperProps}>
            <Input {...props} readOnly className={cn('pr-10 font-mono', props.className)} />
            <Button
                onClick={handleCopy}
                type="button"
                variant={copied ? 'default' : 'outline-solid'}
                title={copied ? formatMessage(generalMessages.copied) : undefined}
                className="absolute top-1 right-1 size-8 rounded-sm p-0"
            >
                <FontAwesomeIcon icon={copied ? ICON_MAP.CHECK : ICON_MAP.COPY} />
            </Button>
        </div>
    );
}

export { InputWithCopy };
